
// Lightning Pattern Generators (3 colors)

import { ColorScheme, createHsl, hslToHex, parseHsl } from "../types/color.model";
import { LightningPattern, randomLightningPattern, randomTwistedStripePattern, TwistedStripePattern } from "../types/pattern.model";
import { addHslVariation, generateBaseHsl, normalizeHue } from "./color.utils";

const generateMonochromaticLightning = (): LightningPattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(createHsl(h, s, Math.max(25, l - 20))), // darker
    addHslVariation(base), // base
    addHslVariation(createHsl(h, s, Math.min(80, l + 15)))  // lighter
  ];

  return {
    outside: hslToHex(colors[0]),
    middle: hslToHex(colors[1]),
    inside: hslToHex(colors[2])
  };
};
  
const generateAnalogousLightning = (): LightningPattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
      addHslVariation(createHsl(normalizeHue(h - 30), s, l)),
      addHslVariation(base),
      addHslVariation(createHsl(normalizeHue(h + 30), s, l))
  ];

  return {
    outside: hslToHex(colors[0]),
    middle: hslToHex(colors[1]),
    inside: hslToHex(colors[2])
  };
};

const generateComplementaryLightning = (): LightningPattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  const complement = createHsl(normalizeHue(h + 180), s, l);
  const { l: compL } = parseHsl(complement);

  const colors = [
    addHslVariation(base),
    addHslVariation(createHsl(normalizeHue(h + 90), s * 0.7, (l + compL) / 2)), // neutral blend
    addHslVariation(complement)
  ];

  return {
    outside: hslToHex(colors[0]),
    middle: hslToHex(colors[1]),
    inside: hslToHex(colors[2])
  };
};
  
const generateSplitComplementaryLightning = (): LightningPattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(base),
    addHslVariation(createHsl(normalizeHue(h + 150), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 210), s, l))
  ];

  return {
    outside: hslToHex(colors[0]),
    middle: hslToHex(colors[1]),
    inside: hslToHex(colors[2])
  };
};
  
const generateTriadicLightning = (): LightningPattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(base),
    addHslVariation(createHsl(normalizeHue(h + 120), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 240), s, l))
  ];

  return {
    outside: hslToHex(colors[0]),
    middle: hslToHex(colors[1]),
    inside: hslToHex(colors[2])
  };
};
  
// Twisted Stripe Pattern Generators (5 colors)

const generateMonochromaticTwistedStripe = (): TwistedStripePattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(createHsl(h, s, Math.max(20, l - 25))), // darkest
    addHslVariation(createHsl(h, s, Math.max(30, l - 12))), // dark
    addHslVariation(base), // base
    addHslVariation(createHsl(h, s, Math.min(75, l + 12))), // light
    addHslVariation(createHsl(h, s, Math.min(85, l + 25)))  // lightest
  ];

  return {
    stripe1: hslToHex(colors[0]),
    stripe2: hslToHex(colors[1]),
    stripe3: hslToHex(colors[2]),
    stripe4: hslToHex(colors[3]),
    stripe5: hslToHex(colors[4])
  };
};
  
const generateAnalogousTwistedStripe = (): TwistedStripePattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(createHsl(normalizeHue(h - 40), s, l)),
    addHslVariation(createHsl(normalizeHue(h - 20), s, l)),
    addHslVariation(base),
    addHslVariation(createHsl(normalizeHue(h + 20), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 40), s, l))
  ];

  return {
    stripe1: hslToHex(colors[0]),
    stripe2: hslToHex(colors[1]),
    stripe3: hslToHex(colors[2]),
    stripe4: hslToHex(colors[3]),
    stripe5: hslToHex(colors[4])
  };
};

const generateComplementaryTwistedStripe = (): TwistedStripePattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  const complement = createHsl(normalizeHue(h + 180), s, l);
  const { s: compS, l: compL } = parseHsl(complement);
  
  const colors = [
    addHslVariation(base),
    addHslVariation(createHsl(h, s, Math.min(75, l + 15))), // lighter base
    addHslVariation(createHsl( // neutral blend
      normalizeHue(h + 90), // perpendicular hue
      (s + compS) / 2 * 0.8,
      (l + compL) / 2
    )),
    addHslVariation(createHsl(normalizeHue(h + 180), s, Math.min(75, compL + 15))), // lighter complement
    addHslVariation(complement)
  ];

  return {
    stripe1: hslToHex(colors[0]),
    stripe2: hslToHex(colors[1]),
    stripe3: hslToHex(colors[2]),
    stripe4: hslToHex(colors[3]),
    stripe5: hslToHex(colors[4])
  };
};

const generateSplitComplementaryTwistedStripe = (): TwistedStripePattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(base),
    addHslVariation(createHsl(normalizeHue(h + 150), s, l)),
    addHslVariation(createHsl( // middle neutral
      normalizeHue(h + 60),
      s * 0.6,
      l
    )),
    addHslVariation(createHsl(normalizeHue(h + 210), s, l)),
    addHslVariation(createHsl(h, s, Math.max(30, l - 15))) // darker base variant
  ];

  return {
    stripe1: hslToHex(colors[0]),
    stripe2: hslToHex(colors[1]),
    stripe3: hslToHex(colors[2]),
    stripe4: hslToHex(colors[3]),
    stripe5: hslToHex(colors[4])
  };
};

const generateTriadicTwistedStripe = (): TwistedStripePattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(base),
    addHslVariation(createHsl(normalizeHue(h + 120), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 240), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 60), s, l)), // fill gap
    addHslVariation(createHsl(normalizeHue(h + 180), s, l)) // fill gap
  ];

  return {
    stripe1: hslToHex(colors[0]),
    stripe2: hslToHex(colors[1]),
    stripe3: hslToHex(colors[2]),
    stripe4: hslToHex(colors[3]),
    stripe5: hslToHex(colors[4])
  };
};

const generateTetradicTwistedStripe = (): TwistedStripePattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(base),
    addHslVariation(createHsl(normalizeHue(h + 90), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 180), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 270), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 45), s, l)) // additional accent
  ];

  return {
    stripe1: hslToHex(colors[0]),
    stripe2: hslToHex(colors[1]),
    stripe3: hslToHex(colors[2]),
    stripe4: hslToHex(colors[3]),
    stripe5: hslToHex(colors[4])
  };
};

const generateSquareTwistedStripe = (): TwistedStripePattern => {
  const base = generateBaseHsl();
  const { h, s, l } = parseHsl(base);
  
  const colors = [
    addHslVariation(base),
    addHslVariation(createHsl(normalizeHue(h + 90), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 180), s, l)),
    addHslVariation(createHsl(normalizeHue(h + 270), s, l)),
    addHslVariation(createHsl( // center blend
      normalizeHue(h + 45),
      s * 0.7,
      l
    ))
  ];

  return {
    stripe1: hslToHex(colors[0]),
    stripe2: hslToHex(colors[1]),
    stripe3: hslToHex(colors[2]),
    stripe4: hslToHex(colors[3]),
    stripe5: hslToHex(colors[4])
  };
};

// Random shuffle existing colors (not yet wired up to any UI action)
export const shuffleLightningPattern = (pattern: LightningPattern): LightningPattern => {
  const colors = [pattern.outside, pattern.middle, pattern.inside];
  const shuffled = colors.sort(() => Math.random() - 0.5);
  
  return {
    outside: shuffled[0],
    middle: shuffled[1],
    inside: shuffled[2]
  };
};

export const shuffleTwistedStripePattern = (pattern: TwistedStripePattern): TwistedStripePattern => {
  const colors = [pattern.stripe1, pattern.stripe2, pattern.stripe3, pattern.stripe4, pattern.stripe5];
  const shuffled = colors.sort(() => Math.random() - 0.5);
  
  return {
    stripe1: shuffled[0],
    stripe2: shuffled[1],
    stripe3: shuffled[2],
    stripe4: shuffled[3],
    stripe5: shuffled[4]
  };
};

export const generateLightningPattern = (scheme: ColorScheme): LightningPattern => {
  switch (scheme) {
    case "random":
      return randomLightningPattern();
    case "monochromatic":
      return generateMonochromaticLightning();
    case "analogous":
      return generateAnalogousLightning();
    case "complementary":
      return generateComplementaryLightning();
    case "split-complementary":
      return generateSplitComplementaryLightning();
    case "triadic":
      return generateTriadicLightning();
    case "tetradic":
    case "square":
      // For 3-color patterns, tetradic and square both use triadic
      return generateTriadicLightning();
    default:
      return randomLightningPattern();
  }
};
  
export const generateTwistedStripePattern = (scheme: ColorScheme): TwistedStripePattern => {
  switch (scheme) {
    case "random":
    return randomTwistedStripePattern();
    case "monochromatic":
    return generateMonochromaticTwistedStripe();
    case "analogous":
    return generateAnalogousTwistedStripe();
    case "complementary":
    return generateComplementaryTwistedStripe();
    case "split-complementary":
    return generateSplitComplementaryTwistedStripe();
    case "triadic":
    return generateTriadicTwistedStripe();
    case "tetradic":
    return generateTetradicTwistedStripe();
    case "square":
    return generateSquareTwistedStripe();
    default:
    return randomTwistedStripePattern();
  }
};
